import { DensityEnum } from '@shared/enums';
import { Subject, debounceTime, takeUntil } from 'rxjs';

import { CdkScrollable, ScrollDispatcher } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import {
  GalleryView,
  GalleryViewHoverAction,
  SideButtons,
} from '../../interfaces';
import { ThemeService, UiHelpersService } from '../../services';

@Component({
  selector: 'phoenix-gallery-viewer',
  templateUrl: './gallery-viewer.component.html',
  standalone: false,
})
export class GalleryViewerComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  scollContainer = viewChild<CdkScrollable>(CdkScrollable);

  uiHelper = inject(UiHelpersService);
  router = inject(Router);
  changeDetectorRef = inject(ChangeDetectorRef);
  themeService = inject(ThemeService);
  scrollDispatcher = inject(ScrollDispatcher);

  @Input() imageList: GalleryView[] = [];
  @Input() selectedImgId: string | null = null;
  @Input() hoverActions: GalleryViewHoverAction[] = [];
  @Input() sideButtons: SideButtons[] = [];
  @Input() set hoverActionsEnabled(val: boolean) {
    this._hoverActionsEnabled = val;
    this.changeDetectorRef.detectChanges();
  }
  get hoverActionsEnabled(): boolean {
    return this._hoverActionsEnabled;
  }
  private _hoverActionsEnabled = true;

  @Input() itemTemplateRef!: TemplateRef<{ image: GalleryView; index: number }>;
  @Input() displayCheckbox = false;
  @Input() hasInfiniteScroll = false;
  @Input() isLoading = false;

  HOVER_ACTION_MIN_WIDTH = 200;

  @Output() imageClick = new EventEmitter();
  @Output() hoverAction = new EventEmitter();
  @Output() imageError = new EventEmitter();
  @Output() scrollEnd = new EventEmitter();
  @Output() listAction = new EventEmitter<{
    id: string;
    action?: () => void;
  }>();

  numColumns = 4;
  gutterSize = '12';
  destroy$ = new Subject<void>();
  displayedImageLoadError = false;

  constructor() {
    effect(() => {
      switch (this.themeService.densitySignal()) {
        case DensityEnum.DEFAULT:
          this.gutterSize = '12';
          break;
        case DensityEnum.COMPACT:
          this.gutterSize = '5';
          break;
        case DensityEnum.SUPER_COMPACT:
          this.gutterSize = '2';
          break;
      }
    });
  }

  ngOnInit(): void {
    if (this.hasInfiniteScroll) {
      this.scrollDispatcher
        .scrolled()
        .pipe(takeUntil(this.destroy$), debounceTime(200))
        .subscribe(result => {
          const target = result?.getElementRef().nativeElement;
          if (
            result === this.scollContainer() &&
            target &&
            target?.offsetHeight + target?.scrollTop >=
              target?.scrollHeight * 0.9 &&
            !this.isLoading
          ) {
            this.scrollEnd.emit();
          }
        });
    }
  }
  ngAfterViewInit() {
    this.scrollToSelected();
  }

  ngOnChanges() {
    this.scrollToSelected();
  }

  private scrollToSelected() {
    if (!this.selectedImgId) return;
    this.uiHelper.navigateToElement(this.selectedImgId);
  }

  onHoverActionClick(id: string, actionId: string) {
    this.hoverAction.emit({ id, actionId });
  }

  onImageLoadError(e: ErrorEvent, imgData: GalleryView) {
    this.imageError.emit(imgData);
    (e.target as HTMLImageElement).src = this.uiHelper.placeholderPath;

    if (!this.displayedImageLoadError) {
      this.displayedImageLoadError = true;
      this.uiHelper.errorSnackbar(`Some images failed to load.`, 5000);
    }
  }

  onImageClick(imgData: GalleryView) {
    this.imageClick.emit(imgData);
    this.selectedImgId = imgData.id;
  }

  get checkBoxSelected() {
    return this.imageList.some(img => img.selected);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
